<template>
  <div class="fichajes-page">
    <!-- Cabecera -->
    <div class="config-panel-header">
      <h2 class="text-h5 font-weight-bold mb-4" :style="{ color: $getColor('vuetifyColor1') }">
        <v-icon :color="$getColor('vuetifyColor1')" large class="mr-2">mdi-clock-check-outline</v-icon>
        Fichajes
      </h2>
      <p class="text-subtitle-1 text--secondary">
        Consulta, exporta y analiza los fichajes del centro de trabajo.
      </p>
    </div>
    <v-divider class="mb-6"></v-divider>

    <!-- ══ LAYOUT dos columnas en PC/tablet ══ -->
    <div class="fichajes-layout">

      <!-- COLUMNA IZQUIERDA: selector maestro (sticky) -->
      <div class="fichajes-layout--selector">
        <div class="selector-label text-overline text--secondary mb-3">
          <v-icon x-small class="mr-1">mdi-calendar</v-icon>
          Periodo
        </div>

        <!-- Toggle modo -->
        <v-btn-toggle v-model="selectorMode" mandatory rounded color="primary" class="mb-4 d-flex">
          <v-btn value="month" style="flex:1;">
            <v-icon left small>mdi-calendar-month</v-icon>
            Mes
          </v-btn>
          <v-btn value="range" style="flex:1;">
            <v-icon left small>mdi-calendar-range</v-icon>
            Rango
          </v-btn>
        </v-btn-toggle>

        <!-- Month picker -->
        <transition name="fade-slide" mode="out-in">
          <div v-if="selectorMode === 'month'" key="month">
            <v-date-picker v-model="selectedMonthPicker" type="month" locale="es" scrollable
              :color="$getPrimaryButton('color')" full-width></v-date-picker>
          </div>

          <!-- Range picker -->
          <div v-else key="range">
            <v-date-picker v-model="selectedRange" range locale="es" :color="$getPrimaryButton('color')"
              first-day-of-week="1" scrollable full-width show-adjacent-months></v-date-picker>
            <div v-if="selectedRange && selectedRange.length > 0" class="d-flex flex-wrap align-center mt-2"
              style="gap: 6px;">
              <v-chip small :color="$getPrimaryButton('color')" dark>
                <v-icon left x-small>mdi-calendar-start</v-icon>
                {{ selectedRange[0] }}
              </v-chip>
              <v-chip small :color="$getPrimaryButton('color')" dark v-if="selectedRange.length > 1">
                <v-icon left x-small>mdi-calendar-end</v-icon>
                {{ selectedRange[1] }}
              </v-chip>
              <v-btn x-small text @click="selectedRange = []">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </transition>

        <!-- Chip periodo activo -->
        <div v-if="currentDateFrom" class="mt-4">
          <v-chip small outlined :color="$getColor('vuetifyColor1')" class="d-flex justify-center">
            <v-icon left x-small>mdi-check-circle-outline</v-icon>
            Periodo activo
          </v-chip>
        </div>
      </div>

      <!-- COLUMNA DERECHA: tabs + contenido -->
      <div class="fichajes-layout--content">
        <!-- Tabs nav -->
        <div class="mdtec-card no-padding dashboard_box mb-0">
          <v-tabs centered grow background-color="transparent" :color="$getColor('vuetifyColor1')">
            <v-tabs-slider></v-tabs-slider>
            <v-tab @click="activeTab = 0">
              <v-icon class="mr-2">mdi-clock-outline</v-icon>
              FICHAJES
            </v-tab>
            <v-tab @click="onStatsTabClick">
              <v-icon class="mr-2">mdi-chart-bar</v-icon>
              ESTADÍSTICAS GENERALES
            </v-tab>
            <v-tab @click="onAdvancedStatsTabClick">
              <v-icon class="mr-2">mdi-chart-areaspline</v-icon>
              ESTADÍSTICAS AVANZADAS
            </v-tab>
          </v-tabs>
        </div>

        <!-- Contenido tabs -->
        <div class="mdtec-card">
          <!-- TAB FICHAJES -->
          <div v-show="activeTab === 0" class="p-3 pt-4">
            <ExportClockEntries :external-date-from="currentDateFrom" :external-date-to="currentDateTo">
            </ExportClockEntries>
          </div>

          <!-- TAB ESTADÍSTICAS GENERALES (lazy) -->
          <div v-show="activeTab === 1" class="p-3 pt-4">
            <ClockEntriesStats v-if="statsLoaded" :date-from="currentDateFrom" :date-to="currentDateTo"
              :workcenter-id="workcenterId"></ClockEntriesStats>
            <div v-else class="d-flex flex-column align-center justify-center" style="min-height: 260px;">
              <v-icon size="64" color="grey lighten-2">mdi-chart-timeline-variant</v-icon>
              <p class="text--secondary mt-3">Selecciona un periodo para ver las estadísticas</p>
            </div>
          </div>

          <!-- TAB ESTADÍSTICAS AVANZADAS (lazy) -->
          <div v-show="activeTab === 2" class="p-3 pt-4">
            <ClockAdvancedStats v-if="advancedStatsLoaded" :date-from="currentDateFrom" :date-to="currentDateTo"
              :workcenter-id="workcenterId"></ClockAdvancedStats>
            <div v-else class="d-flex flex-column align-center justify-center" style="min-height: 260px;">
              <v-icon size="64" color="grey lighten-2">mdi-chart-areaspline</v-icon>
              <p class="text--secondary mt-3">Selecciona un periodo para ver las estadísticas avanzadas</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import clockEntryService from '@/services/clockentry.service';
import ExportClockEntries from '@/views/_FATDA/components/ExportClockEntries.vue';
import ClockEntriesStats from '@/views/_FATDA/components/ClockEntriesStats.vue';
import ClockAdvancedStats from '@/views/_FATDA/components/ClockAdvancedStats.vue';

export default {
  name: 'fichajes-view',
  components: { ExportClockEntries, ClockEntriesStats, ClockAdvancedStats },
  data() {
    return {
      workcenterId: null,
      activeTab: 0,
      statsLoaded: false,
      advancedStatsLoaded: false,
      selectorMode: 'month',
      selectedMonthPicker: null,
      selectedRange: [],
    };
  },
  computed: {
    currentDateFrom() {
      if (this.selectorMode === 'month' && this.selectedMonthPicker) {
        return clockEntryService.getDateTimeRange(this.selectedMonthPicker, 'YYYY-MM').dateFrom;
      }
      if (this.selectorMode === 'range' && this.selectedRange.length === 2) {
        const sorted = [...this.selectedRange].sort();
        return clockEntryService.getDateTimeRange(sorted, '[YYYY-MM-DD,YYYY-MM-DD]').dateFrom;
      }
      return null;
    },
    currentDateTo() {
      if (this.selectorMode === 'month' && this.selectedMonthPicker) {
        return clockEntryService.getDateTimeRange(this.selectedMonthPicker, 'YYYY-MM').dateTo;
      }
      if (this.selectorMode === 'range' && this.selectedRange.length === 2) {
        const sorted = [...this.selectedRange].sort();
        return clockEntryService.getDateTimeRange(sorted, '[YYYY-MM-DD,YYYY-MM-DD]').dateTo;
      }
      return null;
    },
  },
  mounted() {
    if (this.$store.state.projectId === null) {
      if (this.$route.name !== 'SelectWorkCenter') {
        this.$router.push({ name: 'SelectWorkCenter' }).catch(() => {});
      }
    } else {
      this.workcenterId = this.$store.state.projectId;
    }
  },
  methods: {
    onStatsTabClick() {
      this.activeTab = 1;
      if (!this.statsLoaded) this.statsLoaded = true;
    },
    onAdvancedStatsTabClick() {
      this.activeTab = 2;
      if (!this.advancedStatsLoaded) this.advancedStatsLoaded = true;
    },
  },
  watch: {
    selectorMode() {
      this.selectedRange = [];
      this.selectedMonthPicker = null;
    },
  },
};
</script>

<style scoped lang="scss">
.fichajes-layout {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 32px;
  }

  &--selector {
    flex: 0 0 auto;
    width: 100%;

    @media (min-width: 768px) {
      width: 320px;
      position: sticky;
      top: 16px;
    }
  }

  &--content {
    flex: 1 1 0;
    min-width: 0;
  }
}

// Transiciones
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
